import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { UserService } from '../pages/service/user.service';
import { InMemoryStorageService } from '../services/in-memory-storage.service';
import { ItemDetailsService } from '../services/item-details.service';
import { LoaderService } from '../services/loader.service';

export type ItemLastSaleRow = {
    customerNumber?: string;
    customerName?: string;
    orderNumber?: string;
    description?: string;
    quantity?: string;
    salesPrice?: string;
    orderDate?: string;
};

export type ItemOpenOrderRow = {
    orderNumber?: string;
    orderMovingStatus?: string;
    customerId?: string;
    customerName?: string;
    qty?: number;
};

@Component({
    selector: 'app-item-last-sales',
    standalone: true,
    imports: [CommonModule, FormsModule, ButtonModule, AutoCompleteModule, TableModule],
    templateUrl: './item-last-sales.component.html',
    styleUrls: ['./item-last-sales.component.scss']
})
export class ItemLastSalesComponent {
    company = '';
    division = '';
    warehouse = '';

    selectedSuggestion: any = null;
    suggestions: any[] = [];
    searchingItems = false;
    selectedItemNo = '';
    loading = false;
    searched = false;
    errorMessage = '';
    /** lastSold reads M3 history. stock reads open Torna orders holding the item. */
    view: 'lastSold' | 'stock' = 'lastSold';
    setView(next: 'lastSold' | 'stock'): void {
        if (this.view === next) {
            return;
        }
        this.view = next;
        this.onViewChange();
    }
    rows: ItemLastSaleRow[] = [];
    stockRows: ItemOpenOrderRow[] = [];
    readonly pageSize = 10;
    maxRecords = 10;
    readonly maxRecordOptions = [
        { value: 10, label: 'Last 10' },
        { value: 20, label: 'Last 20' },
        { value: 0, label: 'All' }
    ];
    private requestId = 0;
    private suggestionRequestId = 0;

    constructor(
        private readonly itemDetailsService: ItemDetailsService,
        private readonly userService: UserService,
        private readonly inMemoryStorage: InMemoryStorageService,
        private readonly loaderService: LoaderService,
        private readonly router: Router
    ) {
        this.company = String(this.userService.getCompanyId() || '');
        this.division = String(this.userService.getDivisionId() || '');
        this.warehouse = String(this.inMemoryStorage.get('warehouseId') || '');
    }

    goToOrders(): void {
        this.router.navigate(['/Orders1']);
    }

    get canSearch(): boolean {
        return !!this.selectedItemNo && !this.loading;
    }

    get selectedItemDescription(): string {
        const full = String(this.selectedSuggestion?.FINALRESPNSE ?? '').trim();
        const code = this.selectedItemNo.trim();
        if (full && code && (full.startsWith(code + ' | ') || full.startsWith(code + ' '))) {
            return full.slice(code.length).replace(/^\s*\|\s*/, '').trim() || full;
        }
        return full || code;
    }

    get selectedItemMeta(): string {
        if (!this.selectedItemNo) {
            return '';
        }
        const desc = this.selectedItemDescription;
        if (desc && desc !== this.selectedItemNo) {
            return `${this.selectedItemNo} | ${desc}`;
        }
        return this.selectedItemNo;
    }

    get searchHint(): string {
        if (this.searchingItems) {
            return 'Searching items…';
        }
        if (!this.selectedItemNo) {
            return 'Type at least 3 characters, then select an item.';
        }
        return '';
    }

    onViewChange(): void {
        this.rows = [];
        this.stockRows = [];
        this.searched = false;
        this.errorMessage = '';
        if (this.selectedItemNo) {
            this.search();
        }
    }

    searchItems(event: { query?: string }): void {
        const query = String(event?.query ?? '').trim();
        if (this.selectedItemNo && !this.queryMatchesSelectedItem(query)) {
            this.clearSelectedItem();
        }
        if (query.length < 3) {
            this.suggestions = [];
            this.searchingItems = false;
            return;
        }
        const requestId = ++this.suggestionRequestId;
        this.searchingItems = true;
        this.itemDetailsService.searchItemsV2(query + '*', this.company, this.division, this.warehouse).subscribe({
            next: (resp) => {
                if (requestId !== this.suggestionRequestId) {
                    return;
                }
                this.suggestions = this.formatSuggestions(resp);
                this.searchingItems = false;
            },
            error: () => {
                if (requestId !== this.suggestionRequestId) {
                    return;
                }
                this.suggestions = [];
                this.searchingItems = false;
                this.errorMessage = 'Unable to search items. Please try again.';
            }
        });
    }

    onItemSelected(event: any): void {
        const item = event?.value ?? event;
        const itemNo = String(item?.MBITNO ?? item?.itemNumber ?? '').trim();
        if (!itemNo) {
            this.errorMessage = 'Selected item has no item number.';
            this.selectedItemNo = '';
            return;
        }
        this.selectedSuggestion = item;
        this.selectedItemNo = itemNo;
        this.errorMessage = '';
        this.search();
    }

    onSearchModelChange(value: any): void {
        this.selectedSuggestion = value;
        const typed = this.currentQuery(value);
        if (this.selectedItemNo && (!typed || !this.queryMatchesSelectedItem(typed))) {
            this.clearSelectedItem();
        }
    }

    search(): void {
        const item = this.selectedItemNo.trim();
        const division = String(this.division || '').trim();
        if (!item || !division || (this.view === 'stock' && !String(this.company || '').trim())) {
            this.errorMessage = !division
                ? 'Division is required.'
                : this.view === 'stock' && !String(this.company || '').trim()
                  ? 'Company is required.'
                  : 'Select an item from the list.';
            this.rows = [];
            this.stockRows = [];
            this.searched = false;
            return;
        }
        const requestId = ++this.requestId;
        this.loading = true;
        this.errorMessage = '';
        this.searched = false;
        this.rows = [];
        this.stockRows = [];
        const loaderTitle = this.view === 'stock' ? 'Loading open orders' : 'Loading past sales';
        this.loaderService.show(loaderTitle, 'Please wait...');
        const request =
            this.view === 'stock'
                ? this.itemDetailsService.getItemOpenOrders(this.company, division, item)
                : this.itemDetailsService.getItemLastSales(this.company, division, item, this.maxRecords);
        request.subscribe({
            next: (res) => {
                if (requestId !== this.requestId) {
                    return;
                }
                this.loading = false;
                this.loaderService.hide();
                this.searched = true;
                const list = Array.isArray(res?.rows) ? res.rows : [];
                if (this.view === 'stock') {
                    this.stockRows = list;
                } else {
                    this.rows = list;
                }
            },
            error: (err) => {
                if (requestId !== this.requestId) {
                    return;
                }
                this.loading = false;
                this.loaderService.hide();
                this.searched = true;
                this.rows = [];
                this.stockRows = [];
                this.errorMessage =
                    err?.error?.message ||
                    err?.message ||
                    (this.view === 'stock'
                        ? 'Unable to load orders holding this item. Please try again.'
                        : 'Unable to load last sales. Please try again.');
            }
        });
    }

    get recordLabel(): string {
        if (this.view === 'stock') {
            const count = this.stockRows.length;
            const lines = count === 1 ? '1 open order line' : `${count} open order lines`;
            return `${lines} · Total qty ${this.formatQty(String(this.stockTotalQty))}`;
        }
        const count = this.rows.length;
        return count === 1 ? '1 sale' : `${count} sales`;
    }

    get stockTotalQty(): number {
        return this.stockRows.reduce((sum, row) => sum + (Number(row?.qty) || 0), 0);
    }

    customerLabel(row: { customerName?: string; customerNumber?: string; customerId?: string }): string {
        const name = String(row?.customerName || '').trim();
        const number = String(row?.customerNumber || row?.customerId || '').trim();
        if (name && number) {
            return `${name} (${number})`;
        }
        return name || number || '—';
    }

    /** Show moving status codes as stored (CRT, PKL, WIP, …). */
    statusLabel(status: string | undefined): string {
        const code = String(status || '').trim().toUpperCase();
        return code || '—';
    }

    clearItem(): void {
        this.suggestionRequestId++;
        this.selectedSuggestion = null;
        this.suggestions = [];
        this.searchingItems = false;
        this.clearSelectedItem();
    }

    private clearSelectedItem(): void {
        this.requestId++;
        this.selectedItemNo = '';
        this.rows = [];
        this.stockRows = [];
        this.searched = false;
        if (this.loading) {
            this.loading = false;
            this.loaderService.hide();
        }
    }

    private currentQuery(value: any): string {
        if (value == null) {
            return '';
        }
        if (typeof value === 'string') {
            return value.trim();
        }
        return String(value?.FINALRESPNSE ?? value?.MBITNO ?? '').trim();
    }

    private queryMatchesSelectedItem(query: string): boolean {
        const selected = this.selectedItemNo.trim();
        if (!selected || !query) {
            return false;
        }
        return query === selected || query.startsWith(selected + ' | ') || query.startsWith(selected + ' ');
    }

    private formatSuggestions(resp: any[]): any[] {
        if (!Array.isArray(resp) || resp.length === 0) {
            return [];
        }
        return resp.map((item) => {
            const itno = String(item?.MBITNO ?? '').trim();
            const label = String(item?.FINALRESPNSE ?? '').trim();
            if (!itno || !label) {
                return item;
            }
            const separator = ' | ';
            if (label.startsWith(itno + separator) || label.startsWith(itno + ' ')) {
                return item;
            }
            return { ...item, FINALRESPNSE: `${itno}${separator}${label}` };
        });
    }

    onMaxRecordsChange(): void {
        if (this.selectedItemNo) {
            this.search();
        }
    }

    formatQty(value: string | number | undefined): string {
        const raw = String(value ?? '').trim();
        if (!raw) {
            return '';
        }
        const n = Number(raw);
        if (!Number.isFinite(n)) {
            return raw;
        }
        return String(n);
    }

    formatPrice(value: string | undefined): string {
        const raw = String(value ?? '').trim();
        if (!raw) {
            return '';
        }
        const n = Number(raw);
        if (!Number.isFinite(n)) {
            return raw;
        }
        return n.toFixed(2);
    }

    formatDate(value: string | undefined): string {
        const raw = String(value ?? '').trim();
        if (!/^\d{8}$/.test(raw)) {
            return raw;
        }
        const year = Number(raw.slice(0, 4));
        const month = Number(raw.slice(4, 6)) - 1;
        const day = Number(raw.slice(6, 8));
        const date = new Date(year, month, day);
        if (Number.isNaN(date.getTime())) {
            return raw;
        }
        return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    }
}
